import React from "react"
import { useNavigate } from "react-router-dom"
import Button from "@/components/ui/button"
import { useTranslation } from "react-i18next"
import styles from "./ErrorPage.module.scss"

interface ErrorPageProps {
  errorCode: string
  titleKey: string
  titleDefault: string
  descriptionKey: string
  descriptionDefault: string
  buttonKey: string
  buttonDefault: string
  titleSize?: string
}

// Floating particle component for decoration
const FloatingParticle: React.FC<{
  size: number
  top: string
  left: string
  delay: number
  duration: number
}> = ({ size, top, left, delay, duration }) => (
  <div
    style={{
      position: "absolute",
      top,
      left,
      width: size,
      height: size,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(231, 33, 43, 0.3) 0%, transparent 70%)",
      animation: `float ${duration}s ease-in-out ${delay}s infinite`,
      pointerEvents: "none",
    }}
  />
)

const ErrorPage: React.FC<ErrorPageProps> = ({
  errorCode,
  titleKey,
  titleDefault,
  descriptionKey,
  descriptionDefault,
  buttonKey,
  buttonDefault,
  titleSize = "10rem",
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Generate random particles for decoration
  const particles = [
    { size: 80, top: "10%", left: "15%", delay: 0, duration: 6 },
    { size: 60, top: "70%", left: "10%", delay: 1, duration: 8 },
    { size: 100, top: "20%", left: "80%", delay: 2, duration: 7 },
    { size: 50, top: "60%", left: "85%", delay: 0.5, duration: 9 },
    { size: 40, top: "80%", left: "50%", delay: 1.5, duration: 6 },
    { size: 70, top: "5%", left: "60%", delay: 3, duration: 8 },
  ]

  return (
    <div className={styles.errorPage}>
      <div className={styles.errorPageBackground}>
        <div className={styles.errorPageLight} />
        <div className={styles.errorPageGrid} />
        {/* Floating particles */}
        {particles.map((particle, index) => (
          <FloatingParticle key={index} {...particle} />
        ))}
      </div>
      <div className={styles.errorPageContent}>
        <div className={styles.errorPageCodeContainer}>
          <h1 className={styles.errorPageTitle} style={{ fontSize: titleSize }}>
            {errorCode}
          </h1>
        </div>
        <h2 className={styles.errorPageSubtitle}>{t(titleKey, titleDefault)}</h2>
        <p className={styles.errorPageDescription}>{t(descriptionKey, descriptionDefault)}</p>

        <div className={styles.errorPageActions}>
          <Button variant="contained" onClick={() => navigate("/dashboard")}>
            {t(buttonKey, buttonDefault)}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ErrorPage

